import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff, CheckCircle2, AlertCircle, KeyRound } from "lucide-react";
import { api } from "../lib/api";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Link inválido — nenhum token fornecido.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("A palavra-passe deve ter pelo menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("As duas palavras-passe não coincidem.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível redefinir a palavra-passe.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-ronil-light px-6 py-12">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-32 w-96 h-96 rounded-full bg-ronil-navy/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-ronil-sand/10 blur-3xl" />
      </div>

      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-elevation p-8 sm:p-10"
        data-testid="reset-form"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-ronil-navy text-white font-display font-bold text-lg rounded-sm flex items-center justify-center">R</div>
          <div>
            <div className="font-heading font-black text-base tracking-[0.1em] text-ronil-navy">RONIL</div>
            <div className="text-[9px] text-slate-500 uppercase tracking-[0.3em]">Command Center</div>
          </div>
        </div>

        <div className="mb-7">
          <div className="text-[10px] uppercase tracking-[0.25em] text-ronil-sand font-heading font-bold mb-2 flex items-center gap-2">
            <KeyRound className="w-3 h-3" /> Nova palavra-passe
          </div>
          <h1 className="font-display text-3xl font-bold text-ronil-navy tracking-tight mb-2 leading-tight">Defina uma nova senha</h1>
          <p className="text-sm text-slate-500 leading-relaxed">
            Escolha uma palavra-passe forte com pelo menos 8 caracteres.
          </p>
        </div>

        {done ? (
          <div data-testid="reset-success" className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-900">
              <div className="font-heading font-semibold mb-1">Palavra-passe actualizada</div>
              <div className="text-emerald-800/90">A redireccionar para o login…</div>
            </div>
          </div>
        ) : (
          <>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-2">Nova palavra-passe</label>
            <div className="relative mb-5">
              <input
                data-testid="reset-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3.5 py-2.5 pr-10 border border-slate-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-ronil-navy/30 focus:border-ronil-navy transition"
              />
              <button
                type="button"
                data-testid="reset-toggle-password"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Esconder palavra-passe" : "Mostrar palavra-passe"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ronil-navy p-1 transition-colors"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-2">Confirmar palavra-passe</label>
            <input
              data-testid="reset-confirm-input"
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              className="w-full mb-5 px-3.5 py-2.5 border border-slate-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-ronil-navy/30 focus:border-ronil-navy transition"
            />

            {error && (
              <div data-testid="reset-password-error" className="mb-5 flex items-start gap-2 px-4 py-2.5 bg-red-50 text-red-800 border border-red-200 rounded-md text-sm">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              data-testid="reset-submit-button"
              type="submit"
              disabled={submitting || !token}
              className="w-full bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md py-2.5 text-sm font-heading font-semibold tracking-wide transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm hover:shadow-elevation"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Guardar nova palavra-passe
            </button>
          </>
        )}

        <Link
          to="/login"
          data-testid="reset-back-link"
          className="mt-7 inline-block text-xs text-ronil-navy hover:text-ronil-navy-hover hover:underline font-medium"
        >
          Voltar ao login
        </Link>
      </form>
    </div>
  );
}
